import * as React from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { cn, sanitizeHtml } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "../ui/badge";
import { Label } from "../ui/label";
import { useMemo } from "react";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import { CircleAlert } from "lucide-react";

export type Option = {
  value: number | string;
  label: string;
};

export function MultiSelectCombobox({
  options,
  placeholder,
  selected,
  value,
  onChange,
  className,
  description,
  id,
  label,
  htmlContent,
  overwritten = false,
}: {
  options: Option[];
  placeholder: string;
  selected?: (number | string)[] | string;
  value?: (number | string)[] | string;
  onChange?: (value: (number | string)[]) => void;
  className?: string;
  description?: string;
  id: string;
  label: React.ReactNode;
  htmlContent?: string;
  overwritten?: boolean;
}) {
  const [open, setOpen] = React.useState(false);
  const selectedValues = useMemo<(number | string)[]>(() => {
    const source = selected ?? value ?? [];
    return typeof source === "string" ? source.split(",").map((item) => item.trim()) : source;
  }, [selected, value]);
  // FIXME: We should sanitize this HTML content to prevent XSS attacks.
  const sanitizedHtml = htmlContent ? sanitizeHtml(htmlContent) : null;

  const handleUnselect = (item: number | string) => {
    onChange?.(selectedValues.filter((i) => i !== item));
  };

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <div className="flex items-center gap-2">
        <Label htmlFor={id}>{label}</Label>
        {overwritten && (
          <Tooltip>
            <TooltipTrigger asChild>
              <CircleAlert className="w-4 h-4 text-orange-500" />
            </TooltipTrigger>
            <TooltipContent>
              <p>This value overwrites the default system value.</p>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between bg-transparent"
          >
            {placeholder}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-(--radix-popover-trigger-width) p-0">
          <Command>
            <CommandInput placeholder={placeholder} />
            <CommandList>
              <CommandEmpty>No item found.</CommandEmpty>
              <CommandGroup>
                {options.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={option.label}
                    onSelect={() => {
                      if (selectedValues.includes(option.value)) {
                        onChange?.(selectedValues.filter((item) => item !== option.value));
                      } else {
                        onChange?.([...selectedValues, option.value]);
                      }
                      setOpen(true);
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        selectedValues.includes(option.value) ? "opacity-100" : "opacity-0"
                      )}
                    />
                    {option.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {selectedValues.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedValues
            .map((item) => options.find((option) => Number(option.value) === Number(item)))
            .filter((option): option is Option => option !== undefined)
            .map((option) => (
              <Badge variant="secondary" key={option.value} className="flex items-center gap-2">
                {option.label}
                <button
                  className="rounded-full outline-none ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleUnselect(option.value);
                  }}
                >
                  <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                </button>
              </Badge>
            ))}
        </div>
      )}
      {description && (
        <div className={cn("text-xs form-description", selectedValues.length > 0 && "mt-2")}>{description}</div>
      )}
      {htmlContent && (
        <div
          className={cn("text-xs form-description", selectedValues.length > 0 && "mt-2")}
          dangerouslySetInnerHTML={{ __html: sanitizedHtml || "" }}
        />
      )}
    </div>
  );
}
